/**
 * Wizard-Schritt 2: Kanal-Akkordeon — pro gewähltem Channel Texttypen +
 * Vorlage je Texttyp (Spec 2026-07-24 §2/§4, ersetzt StepFields+ChannelRecipeRow).
 * Kopfleiste mit Aktionen fuer alle Channels, darunter je Channel ein Akkordeon.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useChannels } from '@/hooks/useChannels'
import { EID } from '@/lib/element-ids.registry'
import type { BulkAttributeCode } from '@/lib/api/channel-content-bulk'

import { useSystemTextTools } from '../hooks/useSystemTextTools'
import { useTextTemplates } from '../hooks/useTextTemplates'
import { ALL_ATTRIBUTE_CODES } from './attributeDefaults'
import { ChannelFieldAccordion } from './ChannelFieldAccordion'
import { StepChannelFieldsToolbar } from './StepChannelFieldsToolbar'
import type { FieldSelection, FieldSelections } from './useFieldSelections'

interface StepChannelFieldsProps {
  selectedChannelIds: string[]
  fieldSelections: FieldSelections
  onFieldChange: (channelId: string, attributeCode: BulkAttributeCode, patch: Partial<FieldSelection>) => void
  productIds?: string[]
}

export function StepChannelFields({
  selectedChannelIds,
  fieldSelections,
  onFieldChange,
  productIds = [],
}: StepChannelFieldsProps) {
  const { t } = useTranslation()
  const { data: channels = [] } = useChannels()
  const { data: templates = [] } = useTextTemplates()
  const { data: tools = [] } = useSystemTextTools()
  const [openChannelId, setOpenChannelId] = useState<string | null>(selectedChannelIds[0] ?? null)

  const templateOptions = useMemo(() => templates.map((tpl) => ({ value: tpl.id, label: tpl.name })), [templates])
  const toolOptions = useMemo(() => tools.map((tool) => ({ value: tool.code, label: tool.name })), [tools])
  const selected = channels.filter((c) => selectedChannelIds.includes(c.id))
  const firstChannel = selected[0]

  const selectAllFields = () =>
    selected.forEach((c) => ALL_ATTRIBUTE_CODES.forEach((code) => onFieldChange(c.id, code, { enabled: true })))

  const copyFromFirst = () => {
    if (!firstChannel) return
    const source = fieldSelections[firstChannel.id]
    if (!source) return
    selected
      .filter((c) => c.id !== firstChannel.id)
      .forEach((c) =>
        ALL_ATTRIBUTE_CODES.forEach((code) =>
          onFieldChange(c.id, code, {
            enabled: Boolean(source[code]?.enabled),
            templateId: source[code]?.templateId ?? null,
            systemToolCode: source[code]?.systemToolCode ?? null,
          }),
        ),
      )
  }

  return (
    <section id={EID['text-bulk-wizard-channel-list-region']} className="space-y-3">
      <StepChannelFieldsToolbar
        title={t('channelContent.wizard.fieldsSectionTitle')}
        sourceChannelLabel={firstChannel ? firstChannel.custom_name || firstChannel.channel_name : null}
        onSelectAll={selectAllFields}
        onCopyFromFirst={copyFromFirst}
      />

      <div className="space-y-2">
        {selected.map((channel) => (
          <ChannelFieldAccordion
            key={channel.id}
            channel={channel}
            fields={fieldSelections[channel.id] ?? ({} as Record<BulkAttributeCode, FieldSelection>)}
            onFieldChange={(code, patch) => onFieldChange(channel.id, code, patch)}
            templateOptions={templateOptions}
            toolOptions={toolOptions}
            productIds={productIds}
            open={openChannelId === channel.id}
            onToggle={() => setOpenChannelId((prev) => (prev === channel.id ? null : channel.id))}
          />
        ))}
      </div>
    </section>
  )
}
